import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  CheckCircle2, AlertTriangle, Package,
  ArrowRight, ArrowLeft, Loader2,
  Info, FileText, Check, X, Search, GitCompare, Unlink, RotateCcw
} from 'lucide-react';
import { incomingService, productService, invoiceEntryService } from '../../../../services/erpApi';
import { useToast } from '../../../../hooks/useToast';
import { Toast } from '../../../../components';
import ConfirmModal from '../../../../components/ERP/ConfirmModal/ConfirmModal';
import { usePermissions } from '../../../../hooks/usePermissions';
import { MODULES, ACTIONS } from '../../../../utils/permissions';
import './IncomingValidationPage.css'; // Usar o mesmo CSS da validação

interface InvoiceEntryItem {
  id: number;
  product_id: number;
  product_name?: string;
  quantity: number;
  unit_price: number;
  total_price: number;
  supplier_code?: string;
  xml_barcode?: string;
  xml_description?: string;
  xml_unit?: string;
}

interface RequisitionItem {
  id: number;
  product_id: number;
  product_name_from_catalog?: string;
  product_sku?: string;
  requested_qty?: number;
  unit_price?: number;
  total_price?: number;
  supplier_code?: string;
}

interface InvoiceEntry {
  id: number;
  number: string;
  supplier_id: number;
  supplier_name?: string;
  status: string;
  items?: InvoiceEntryItem[];
  requisitionItems?: RequisitionItem[];
}

const IncomingLaunchPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { toast, showToast, hideToast } = useToast();
  const { hasPermission } = usePermissions();
  const canLaunchStock = hasPermission(MODULES.STOCK, ACTIONS.STOCK_INCOMING_LAUNCH);
  const [invoiceEntry, setInvoiceEntry] = useState<InvoiceEntry | null>(null);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  
  // Estados para modal de vinculação
  const [showLinkModal, setShowLinkModal] = useState(false);
  const [linkingItem, setLinkingItem] = useState<{type: 'xml', item: InvoiceEntryItem, productId?: number} | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState<any[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  
  // Estados para modal de confirmação
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState<{
    title: string;
    message: string;
    type: 'info' | 'success' | 'warning' | 'danger';
    onConfirm: () => void;
  } | null>(null);

  useEffect(() => {
    if (id) {
      loadInvoiceEntry();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  // Buscar produtos quando o termo de busca mudar
  useEffect(() => {
    if (showLinkModal && searchTerm) {
      const timer = setTimeout(() => {
        searchProducts(searchTerm);
      }, 300);
      return () => clearTimeout(timer);
    } else {
      setSearchResults([]);
    }
  }, [searchTerm, showLinkModal]);

  const loadInvoiceEntry = async () => {
    try {
      setLoading(true);
      const response = await incomingService.getById(parseInt(id!));
      if (response.success) {
        setInvoiceEntry(response.data);
      } else {
        showToast('Nota fiscal não encontrada', 'error');
        navigate('/erp/stock/incoming');
      }
    } catch (error: any) {
      console.error('Erro ao carregar invoice entry:', error);
      showToast('Erro ao carregar nota fiscal', 'error');
      navigate('/erp/stock/incoming');
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    navigate('/erp/stock/incoming');
  };

  // Função para buscar produtos
  const searchProducts = async (term: string) => {
    if (term.length < 2) {
      setSearchResults([]);
      return;
    }

    setSearchLoading(true);
    try {
      const response = await productService.getAll({ search: term, active: true });
      if (response.success) {
        const searchLower = term.toLowerCase();
        const filtered = (response.data || [])
          .filter((product: any) => {
            // Buscar por nome, SKU, código de barras, descrição ou referências (cProd)
            const matchesName = product.name?.toLowerCase().includes(searchLower);
            const matchesSku = product.sku?.toLowerCase().includes(searchLower);
            const matchesBarcode = product.barcode?.toLowerCase().includes(searchLower);
            const matchesDescription = product.description?.toLowerCase().includes(searchLower);
            
            // Buscar por referências (cProd)
            const matchesReference = product.references && product.references.some((ref: string) => 
              ref.toLowerCase().includes(searchLower)
            );
            
            return matchesName || matchesSku || matchesBarcode || matchesDescription || matchesReference;
          })
          .slice(0, 10);
        setSearchResults(filtered);
      }
    } catch (error) {
      console.error('Erro ao buscar produtos:', error);
      setSearchResults([]);
    } finally {
      setSearchLoading(false);
    }
  };

  // Função para abrir modal de vinculação
  const handleOpenLinkModal = (xmlItem: InvoiceEntryItem, productId?: number) => {
    setLinkingItem({ type: 'xml', item: xmlItem, productId });
    setShowLinkModal(true);
    setSearchTerm('');
    setSearchResults([]);
  };

  // Função para vincular produto
  const handleLinkProduct = async (product: any) => {
    if (!linkingItem || !invoiceEntry || !id) return;

    try {
      const response = await incomingService.linkItem(
        parseInt(id),
        'xml',
        linkingItem.item.id,
        product.id
      );

      if (response.success) {
        showToast(`Produto "${product.name}" vinculado com sucesso!`, 'success');
        setShowLinkModal(false);
        setLinkingItem(null);
        
        // Recarregar dados após vinculação
        await loadInvoiceEntry();
      } else {
        showToast(response.message || 'Erro ao vincular produto', 'error');
      }
    } catch (error: any) {
      console.error('Erro ao vincular produto:', error);
      showToast('Erro ao vincular produto', 'error');
    }
  };

  // Função para desvincular produto
  const handleUnlinkProduct = (xmlItem: InvoiceEntryItem) => {
    if (!xmlItem || !id) return;

    setConfirmModalConfig({
      title: 'Desvincular Item',
      message: 'Tem certeza que deseja desvincular este item? Você precisará vinculá-lo novamente antes de lançar.',
      type: 'warning',
      onConfirm: async () => {
        try {
          const response = await incomingService.unlinkItem(
            parseInt(id!),
            xmlItem.id
          );

          if (response.success) {
            showToast('Item desvinculado com sucesso!', 'success');
            setShowConfirmModal(false);
            setConfirmModalConfig(null);
            
            // Recarregar dados após desvinculação
            await loadInvoiceEntry();
          } else {
            showToast(response.message || 'Erro ao desvincular item', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao desvincular item:', error);
          showToast('Erro ao desvincular item', 'error');
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleLaunch = () => {
    if (!invoiceEntry || !id) return;

    if (!canLaunchStock) {
      showToast('Você não tem permissão para lançar estoque.', 'error');
      return;
    }

    // Validar que todos os itens têm produto associado
    const xmlItems = invoiceEntry.items || [];
    const itemsWithoutProduct = xmlItems.filter(item => !item.product_id);
    if (itemsWithoutProduct.length > 0) {
      showToast(`${itemsWithoutProduct.length} item(ns) sem produto associado. Associe todos os produtos antes de lançar.`, 'error');
      return;
    }

    setConfirmModalConfig({
      title: 'Lançar no Estoque',
      message: `Deseja lançar esta nota fiscal (#${invoiceEntry.number}) no estoque? Esta ação irá adicionar os itens ao estoque do sistema.`,
      type: 'info',
      onConfirm: async () => {
        setProcessing(true);
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
        
        try {
          const response = await invoiceEntryService.process(parseInt(id!));
          
          if (response.success) {
            showToast('Estoque lançado com sucesso!', 'success');
            // Sempre redirecionar para o dashboard após lançar estoque
            navigate('/erp/stock/incoming');
          } else {
            showToast(response.message || 'Erro ao lançar estoque', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao lançar estoque:', error);
          showToast(error.response?.data?.message || 'Erro ao lançar estoque. Tente novamente.', 'error');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  const handleBackToTriagem = () => {
    if (!invoiceEntry || !id) return;

    setConfirmModalConfig({
      title: 'Voltar para Triagem',
      message: `Deseja retornar esta nota fiscal (#${invoiceEntry.number}) para triagem? Esta ação permitirá corrigir erros de conversão de quantidade. Após a correção, a nota irá direto para lançamento (pulando a conferência, pois já foi conferida).`,
      type: 'warning',
      onConfirm: async () => {
        setProcessing(true);
        setShowConfirmModal(false);
        setConfirmModalConfig(null);
        
        try {
          const response = await incomingService.backToTriagem(parseInt(id!), true); // skipInspection = true
          
          if (response.success) {
            showToast('Nota fiscal retornada para triagem com sucesso!', 'success');
            // Sempre redirecionar para o dashboard após voltar para triagem
            navigate('/erp/stock/incoming');
          } else {
            showToast(response.message || 'Erro ao retornar nota para triagem', 'error');
          }
        } catch (error: any) {
          console.error('Erro ao retornar nota para triagem:', error);
          showToast(error.response?.data?.message || 'Erro ao retornar nota para triagem. Tente novamente.', 'error');
        } finally {
          setProcessing(false);
        }
      }
    });
    setShowConfirmModal(true);
  };

  if (loading) {
    return (
      <div className="incoming-validation-loading">
        <Loader2 size={32} className="incoming-validation-spinner" />
        <p>Carregando dados de lançamento...</p>
      </div>
    );
  }

  if (!invoiceEntry) {
    return null;
  }

  const xmlItems = invoiceEntry.items || [];
  const hasUnlinkedItems = xmlItems.some(item => {
    const isRealXmlItem = item && item.id > 0;
    const hasSystemMatch = item && item.product_id != null;
    return !hasSystemMatch && isRealXmlItem;
  });

  // Contar itens OK (vinculados ao sistema)
  const itemsOk = xmlItems.filter(xmlItem => {
    const isRealXmlItem = xmlItem && xmlItem.id > 0;
    const hasSystemMatch = xmlItem && xmlItem.product_id != null;
    return hasSystemMatch && isRealXmlItem;
  }).length;

  return (
    <div className="incoming-validation">
      {/* Tool Header */}
      <div className="incoming-validation-tool-header">
        <div className="incoming-validation-tool-header-left">
          <button 
            onClick={handleBack} 
            className="incoming-validation-tool-back-btn"
          >
            <ArrowLeft size={20} />
          </button>
          <div>
            <div className="incoming-validation-tool-title-row">
              <h1 className="incoming-validation-tool-title">
                Lançamento de Estoque {id}
              </h1>
              <span className="incoming-validation-tool-badge">Lançar Itens no Estoque</span>
            </div>
            <p className="incoming-validation-tool-subtitle">
              Verificação Final e Lançamento de Itens
            </p>
          </div>
        </div>

        <div className="incoming-validation-tool-header-right" style={{ display: 'flex', gap: '0.75rem' }}>
          <button
            onClick={handleBackToTriagem}
            disabled={processing}
            className="incoming-validation-tool-back-triagem-btn"
            title="Voltar para triagem para corrigir erros de conversão"
          >
            {processing ? (
              <>
                <Loader2 size={16} className="incoming-validation-spinner" />
                Processando...
              </>
            ) : (
              <>
                <RotateCcw size={16} />
                Voltar para Triagem
              </>
            )}
          </button>
          <button
            onClick={handleLaunch}
            disabled={processing || hasUnlinkedItems || !canLaunchStock}
            className="incoming-validation-tool-finish-btn"
            title={
              !canLaunchStock
                ? 'Sem permissão para lançar estoque'
                : hasUnlinkedItems
                  ? 'Vincule todos os itens antes de lançar'
                  : ''
            }
          >
            {processing ? (
              <>
                <Loader2 size={16} className="incoming-validation-spinner" />
                Lançando...
              </>
            ) : (
              <>
                <CheckCircle2 size={16} />
                Lançar no Estoque
                <ArrowRight size={16} />
              </>
            )}
          </button>
        </div>
      </div>

      {/* Main Grid Layout */}
      <div className="incoming-validation-main-grid">
        {/* Comparison Table */}
        <div>
          {(xmlItems && xmlItems.length > 0) ? (
            <div className="incoming-validation-comparison-section">
              <div className="incoming-validation-comparison-header">
                <h3>Itens para Lançamento</h3>
                <div className="incoming-validation-comparison-legend">
                  <div className="incoming-validation-legend-item">
                    <span className="incoming-validation-legend-dot match"></span>
                    <span className="incoming-validation-legend-text">Vinculado</span>
                  </div>
                  <div className="incoming-validation-legend-item">
                    <span className="incoming-validation-legend-dot difference"></span>
                    <span className="incoming-validation-legend-text">Não Vinculado</span>
                  </div>
                </div>
              </div>

              <div className="incoming-validation-table-wrapper">
                <table className="incoming-validation-comparison-table">
                  <thead>
                    <tr>
                      <th>Produto / SKU</th>
                      <th className="text-center">Qtd XML</th>
                      <th className="text-center">Status</th>
                      <th className="text-center">Ações</th>
                    </tr>
                  </thead>
                  <tbody>
                    {xmlItems.map((xmlItem) => {
                      const productId = xmlItem.product_id;
                      const hasSystemMatch = xmlItem && xmlItem.product_id != null;
                      const systemProductName = xmlItem?.product_name || null;
                      const isRealXmlItem = xmlItem && xmlItem.id > 0;
                      const isUnlinkedFromSystem = !hasSystemMatch && isRealXmlItem;
                      const xmlQty = Number(xmlItem?.quantity || 0);
                      
                      // Nome do produto do XML (xProd)
                      const productName = xmlItem?.xml_description || 
                        xmlItem?.product_name || 
                        `Produto ${productId ? `#${productId}` : 'não vinculado'}`;
                      const sku = xmlItem?.supplier_code || 'N/A';

                      return (
                        <tr key={xmlItem.id} className={isUnlinkedFromSystem ? 'incoming-validation-row-warning' : ''}>
                          <td>
                            <div className="incoming-validation-table-product">
                              <div className={`incoming-validation-table-product-icon ${hasSystemMatch ? 'match' : 'difference'}`}>
                                <Package size={20} />
                              </div>
                              <div className="incoming-validation-table-product-info">
                                <p className="incoming-validation-table-product-name">{productName}</p>
                                <p className="incoming-validation-table-product-sku">{sku}</p>
                                {hasSystemMatch && systemProductName && (
                                  <div className="incoming-validation-table-product-link">
                                    <GitCompare size={12} />
                                    <span>Vinculado a: {systemProductName}</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          </td>
                          <td className="incoming-validation-table-qty">
                            {isRealXmlItem && xmlQty > 0 ? xmlQty.toFixed(0) : '-'}
                          </td>
                          <td className="incoming-validation-table-status">
                            <div className="incoming-validation-status-container">
                              {hasSystemMatch ? (
                                <div className="incoming-validation-status-badge match">
                                  <Check size={16} strokeWidth={3} />
                                </div>
                              ) : (
                                <div 
                                  className="incoming-validation-status-badge difference has-warning"
                                >
                                  <AlertTriangle size={16} strokeWidth={3} />
                                </div>
                              )}
                            </div>
                          </td>
                          <td className="incoming-validation-table-actions">
                            {isUnlinkedFromSystem && (
                              <button
                                className="incoming-validation-link-btn"
                                onClick={() => handleOpenLinkModal(xmlItem, productId)}
                                title="Vincular item a um produto existente"
                              >
                                <Search size={16} />
                              </button>
                            )}
                            {hasSystemMatch && !isUnlinkedFromSystem && (
                              <div style={{ display: 'flex', gap: '0.5rem', flexDirection: 'column' }}>
                                <button
                                  className="incoming-validation-link-btn"
                                  onClick={() => handleOpenLinkModal(xmlItem, productId)}
                                  title="Alterar vínculo do produto"
                                >
                                  <Search size={16} />
                                </button>
                                <button
                                  className="incoming-validation-unlink-btn"
                                  onClick={() => handleUnlinkProduct(xmlItem)}
                                  title="Desvincular item"
                                >
                                  <Unlink size={16} />
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <div className="incoming-validation-section">
              <div className="incoming-validation-section-header">
                <Info size={20} />
                <h2>Informações</h2>
              </div>
              <div className="incoming-validation-info">
                <p>
                  Esta nota fiscal não possui itens cadastrados.
                </p>
              </div>
            </div>
          )}

          {/* Info Box */}
          <div className={`incoming-validation-info ${hasUnlinkedItems ? 'warning' : ''}`}>
            <Info size={18} />
            <p>
              {hasUnlinkedItems
                ? 'Há itens não vinculados ao sistema. Vincule todos os itens antes de lançar no estoque.'
                : 'Todos os itens estão vinculados ao sistema. Você pode prosseguir com o lançamento.'}
            </p>
          </div>
        </div>

        {/* Sidebar Summary */}
        <div className="incoming-validation-sidebar">
          <div className="incoming-validation-summary-card">
            <h3 className="incoming-validation-summary-title">
              <FileText size={16} />
              Resumo do Lançamento
            </h3>

            <div className="incoming-validation-summary-content">
              <div className="incoming-validation-summary-stats">
                <div className="incoming-validation-summary-stat">
                  <p className="incoming-validation-summary-stat-label">Itens Vinculados</p>
                  <p className={`incoming-validation-summary-stat-value ok`}>
                    {itemsOk}
                  </p>
                </div>
                <div className="incoming-validation-summary-stat">
                  <p className="incoming-validation-summary-stat-label">Não Vinculados</p>
                  <p className={`incoming-validation-summary-stat-value difference`}>
                    {xmlItems.length - itemsOk}
                  </p>
                </div>
              </div>

              {hasUnlinkedItems && (
                <div className="incoming-validation-summary-alert">
                  <div className="incoming-validation-summary-alert-content">
                    <AlertTriangle size={18} />
                    <p className="incoming-validation-summary-alert-text">
                      Atenção: Há itens não vinculados ao sistema. Vincule todos os itens antes de lançar no estoque.
                    </p>
                  </div>
                </div>
              )}

              <div className="incoming-validation-summary-info">
                <div className="incoming-validation-summary-info-content">
                  <Info size={16} />
                  <p className="incoming-validation-summary-info-text">
                    O lançamento no estoque irá adicionar os itens vinculados ao estoque do sistema. 
                    Certifique-se de que todos os itens estão corretamente vinculados antes de prosseguir.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modal de Vinculação */}
      {showLinkModal && linkingItem && (
        <div className="incoming-validation-modal-overlay" onClick={() => setShowLinkModal(false)}>
          <div className="incoming-validation-modal" onClick={(e) => e.stopPropagation()}>
            <div className="incoming-validation-modal-header">
              <h3>Vincular Produto</h3>
              <button 
                className="incoming-validation-modal-close"
                onClick={() => setShowLinkModal(false)}
              >
                <X size={20} />
              </button>
            </div>

            <div className="incoming-validation-modal-content">
              <div className="incoming-validation-modal-item-info">
                <p className="incoming-validation-modal-item-label">Item do XML:</p>
                <p className="incoming-validation-modal-item-name">
                  {linkingItem.item?.xml_description || linkingItem.item?.product_name || linkingItem.item?.description || 'N/A'}
                </p>
                {linkingItem.item?.supplier_code && (
                  <p className="incoming-validation-modal-item-detail">
                    Código (cProd): {linkingItem.item.supplier_code}
                  </p>
                )}
                {linkingItem.item?.xml_barcode && linkingItem.item.xml_barcode !== 'SEM GTIN' && (
                  <p className="incoming-validation-modal-item-detail">
                    Código de Barras: {linkingItem.item.xml_barcode}
                  </p>
                )}
                {linkingItem.item?.quantity && (
                  <p className="incoming-validation-modal-item-detail">
                    Quantidade: {Number(linkingItem.item.quantity).toFixed(2)}
                  </p>
                )}
              </div>

              <div className="incoming-validation-modal-search">
                <div className="incoming-validation-modal-search-input-wrapper">
                  <Search size={20} className="incoming-validation-modal-search-icon" />
                  <input
                    type="text"
                    className="incoming-validation-modal-search-input"
                    placeholder="Buscar produto por nome, SKU, código de barras ou referência (cProd)..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    autoFocus
                  />
                  {searchLoading && <Loader2 size={20} className="incoming-validation-spinner" />}
                </div>

                {searchResults.length > 0 && (
                  <div className="incoming-validation-modal-results">
                    {searchResults.map((product) => (
                      <div
                        key={product.id}
                        className="incoming-validation-modal-result-item"
                        onClick={() => handleLinkProduct(product)}
                      >
                        <div className="incoming-validation-modal-result-info">
                          <p className="incoming-validation-modal-result-name">{product.name}</p>
                          <p className="incoming-validation-modal-result-sku">SKU: {product.sku || 'N/A'}</p>
                          {product.barcode && (
                            <p className="incoming-validation-modal-result-barcode">Cód. Barras: {product.barcode}</p>
                          )}
                          {product.references && product.references.length > 0 && (
                            <p className="incoming-validation-modal-result-reference">
                              Referências: {product.references.join(', ')}
                            </p>
                          )}
                        </div>
                        <div className="incoming-validation-modal-result-action">
                          <Search size={20} />
                        </div>
                      </div>
                    ))}
                  </div>
                )}

                {searchTerm.length >= 2 && !searchLoading && searchResults.length === 0 && (
                  <div className="incoming-validation-modal-no-results">
                    <p>Nenhum produto encontrado</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal de Confirmação */}
      {showConfirmModal && confirmModalConfig && (
        <ConfirmModal
          isOpen={showConfirmModal}
          onClose={() => {
            setShowConfirmModal(false);
            setConfirmModalConfig(null);
          }}
          onConfirm={confirmModalConfig.onConfirm}
          title={confirmModalConfig.title}
          message={confirmModalConfig.message}
          type={confirmModalConfig.type}
          confirmText="Confirmar"
          cancelText="Cancelar"
        />
      )}

      {/* Toast */}
      {toast.show && (
        <Toast
          message={toast.message}
          type={toast.type}
          onClose={hideToast}
        />
      )}
    </div>
  );
};

export default IncomingLaunchPage;
